import { useTranslation } from 'react-i18next';

/**
 * Disclaimer Section Component
 * Displays event disclaimer and photo usage authorization
 */
export default function Disclaimer() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  return (
    <section className="bg-[#0A2540]/95 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Disclaimer Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2C7DA0]/20">
            <i className="ri-shield-check-line text-xl text-[#2C7DA0]" />
          </div>
          <h2 className="text-xl font-semibold">
            {isZh ? '活动声明' : 'Event Disclaimer'}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Disclaimer Content */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-start gap-3 mb-4">
              <i className="ri-error-warning-line text-lg text-[#2C7DA0] mt-1" />
              <h3 className="font-medium text-lg">
                {isZh ? '免责声明' : 'Disclaimer'}
              </h3>
            </div>
            <ul className="space-y-3 text-sm text-white/80 leading-relaxed">
              {isZh ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>本活动由全球科创圆桌联盟（GTRA）主办，主办方保留对活动内容、时间、地点及嘉宾的最终调整权。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>参会者需遵守当地法律法规及活动现场规定，对自身行为负责。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>主办方不对参会者因不可抗力（包括但不限于自然灾害、政府行为、社会异常事件等）导致的活动变更或取消承担赔偿责任。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>活动期间的个人财产及人身安全由参会者自行负责，活动现场将提供必要的安全提示和紧急联络方式。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>本声明的解释权归全球科创圆桌联盟（GTRA）所有，如有争议将依据适用法律进行解决。</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>This event is organized by the Global Tech Roundtable Alliance (GTRA). The organizer reserves the right to make final adjustments to event content, time, location, and speakers.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>Participants must comply with local laws and regulations as well as venue rules, and are responsible for their own conduct.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>The organizer shall not be liable for any activity changes or cancellations caused by force majeure (including but not limited to natural disasters, government actions, social anomalies, etc.).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>Participants are responsible for their personal property and safety during the event. The venue will provide necessary safety information and emergency contact details.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>The right of final interpretation belongs to the Global Tech Roundtable Alliance (GTRA). Any disputes shall be resolved in accordance with applicable laws.</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Photo Authorization Content */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-start gap-3 mb-4">
              <i className="ri-camera-line text-lg text-[#2C7DA0] mt-1" />
              <h3 className="font-medium text-lg">
                {isZh ? '照片使用授权' : 'Photo Usage Authorization'}
              </h3>
            </div>
            <ul className="space-y-3 text-sm text-white/80 leading-relaxed">
              {isZh ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>活动期间，主办方可能会拍摄照片、视频及进行网络直播，用于活动记录、宣传推广及媒体报道。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>参会者参与本次活动即表示同意主办方使用其在活动中的影像资料（照片、视频等）用于GTRA的官方宣传渠道，包括但不限于官网、社交媒体、宣传册及新闻发布。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>如参会者不希望被拍摄或录像，请在活动现场主动告知工作人员或佩戴"不拍摄"标识贴纸。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>第三方媒体采访需获得受访者本人同意，主办方不对第三方媒体的使用行为承担责任。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>如需撤回授权或对已发布的影像资料提出异议，请联系 info@gtraweb3.com。</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>During the event, the organizer may take photos, videos, and conduct live streaming for event records, promotional purposes, and media coverage.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>By participating in this event, attendees consent to the organizer's use of their images (photos, videos, etc.) in GTRA's official promotional channels, including but not limited to the official website, social media, brochures, and press releases.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>If you do not wish to be photographed or recorded, please inform the event staff or wear a "No Photography" sticker at the venue.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>Third-party media interviews require the consent of the interviewee. The organizer is not responsible for third-party media usage.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#2C7DA0] mt-1">•</span>
                    <span>To withdraw consent or object to published materials, please contact info@gtraweb3.com.</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-6 pt-6 border-t border-white/10 text-center">
          <p className="text-white/60 text-sm">
            {isZh ? '如有疑问，请联系：' : 'For questions, please contact: '}
            <a 
              href="mailto:info@gtraweb3.com" 
              className="text-[#2C7DA0] hover:text-[#2C7DA0]/80 transition-colors"
            >
              info@gtraweb3.com
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
